import { useEffect, useRef } from 'react';
import type { ImageEngine, RenderParams } from '@/tools/image-engine';

const DEBOUNCE_MS = 120;

/**
 * The engine's render of `params`, redrawn when they change (debounced; the engine keeps at most
 * one render in flight and drops the ones a newer request replaces).
 */
export function LivePreview({
  engine,
  params,
  label,
  className,
}: {
  readonly engine: () => ImageEngine;
  readonly params: RenderParams | null;
  readonly label: string;
  readonly className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const key = params ? JSON.stringify(params) : '';

  useEffect(() => {
    if (!params) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      engine()
        .renderPreview(params)
        .then((preview) => {
          const canvas = canvasRef.current;
          if (!cancelled && canvas) {
            canvas.width = preview.width;
            canvas.height = preview.height;
            canvas.getContext('2d')?.drawImage(preview.image, 0, 0);
          }
          preview.release();
        })
        .catch(() => {
          // Superseded or failed: the previous render stays on screen.
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `key` stands for `params`, compared by value.
  }, [key, engine]);

  return <canvas ref={canvasRef} role="img" aria-label={label} className={className} />;
}
